import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'
import { extractErrorMessage } from '@joyful/utils'

import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Fieldset,
  Form,
  FormTextareaField,
  Stack,
  toast,
} from '../../../../core'
import { useApiQueryClient } from '../../../utils/reactQuery'
import { useCurrentUser } from '../../../utils/currentUserContext'
import { useSafeApiOrgSlug } from '../../../utils/orgSlug'
import { useFeedbackPageUrl } from './FeedbackPageUrlContext'
import type { UserFeedbackDialogController } from '../hooks/useUserFeedbackDialog'
import { stripDialogQueryParams } from '../hooks/useUserFeedbackDialog'
import {
  handleScreenshotAttachmentPaste,
  ScreenshotAttachmentField,
} from './ScreenshotAttachmentField'

const FeedbackScreenshotFileName = 'feedback-screenshot.png'

const UserFeedbackFormSchema = z.object({
  feedback: z.string().trim().min(1, `Feedback is required`),
})

type UserFeedbackFormValues = z.infer<typeof UserFeedbackFormSchema>

export interface UserFeedbackDialogProps {
  feedbackDialog: UserFeedbackDialogController
}

export function UserFeedbackDialog({
  feedbackDialog,
}: UserFeedbackDialogProps) {
  // The navbar mounts this dialog on every page, including routes without
  // an org slug in the URL (e.g. /agent-skills). Fall back to the user's
  // first account so feedback still has an account to attribute.
  const activeOrgSlug = useSafeApiOrgSlug()
  const currentUser = useCurrentUser()
  const apiOrgSlug = activeOrgSlug ?? currentUser.accounts.at(0)?.slug ?? null

  if (apiOrgSlug === null) {
    return null
  }

  return (
    <UserFeedbackDialogContent
      apiOrgSlug={apiOrgSlug}
      feedbackDialog={feedbackDialog}
    />
  )
}

function UserFeedbackDialogContent({
  apiOrgSlug,
  feedbackDialog,
}: UserFeedbackDialogProps & { apiOrgSlug: string }) {
  const { fetchClient } = useApiQueryClient()
  const feedbackPageUrl = useFeedbackPageUrl()
  const form = useForm<UserFeedbackFormValues>({
    defaultValues: {
      feedback: '',
    },
    resolver: zodResolver(UserFeedbackFormSchema),
  })

  const feedbackMutation = useMutation({
    mutationFn: async (values: UserFeedbackFormValues) => {
      const pageUrl = stripDialogQueryParams(
        feedbackPageUrl ??
          feedbackDialog.screenshot?.pageUrl ??
          window.location.href,
      )
      const formData = new FormData()
      formData.append('feedback', values.feedback)
      formData.append('page_url', pageUrl)
      if (feedbackDialog.screenshot !== null) {
        const screenshotFile = new File(
          [feedbackDialog.screenshot.blob],
          FeedbackScreenshotFileName,
          {
            type: feedbackDialog.screenshot.blob.type || 'image/png',
          },
        )
        formData.append('screenshot', screenshotFile)
      }

      return fetchClient.POST('/{org_slug}/feedback', {
        body: {
          feedback: values.feedback,
          page_url: pageUrl,
          screenshot: feedbackDialog.screenshot === null ? undefined : '',
        },
        bodySerializer: () => {
          return formData
        },
        params: {
          path: {
            org_slug: apiOrgSlug,
          },
        },
      })
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error))
    },
    onSuccess: () => {
      toast.success(`Feedback sent`)
      form.reset()
      feedbackDialog.closeFeedbackDialog()
    },
  })

  return (
    <Dialog
      open={feedbackDialog.isDialogOpen}
      onOpenChange={(nextOpen) => {
        if (feedbackMutation.isPending) {
          return
        }

        if (nextOpen === false) {
          feedbackDialog.closeFeedbackDialog()
        }
      }}
    >
      <DialogContent asChild className="z-50 max-w-2xl">
        <Form
          shouldSubmitWithHotkey
          onPaste={(event) => {
            handleScreenshotAttachmentPaste({
              event,
              isDisabled: feedbackMutation.isPending,
              screenshotDialog: feedbackDialog,
            })
          }}
          onSubmit={form.handleSubmit((values) => {
            feedbackMutation.mutate(values)
          })}
        >
          <FormProvider {...form}>
            <DialogHeader hasDivider>
              <DialogTitle>{`Leave feedback`}</DialogTitle>
              <DialogDescription>
                {`Tell us what happened and what we can improve.`}
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <Fieldset disabled={feedbackMutation.isPending}>
                <Stack gap="md">
                  <ScreenshotAttachmentField
                    isDisabled={feedbackMutation.isPending}
                    screenshotDialog={feedbackDialog}
                  />
                  <FormTextareaField
                    control={form.control}
                    name="feedback"
                    label={`Feedback`}
                    isRequired
                    orientation="vertical"
                    textareaProps={{
                      placeholder: `Describe what happened or what could be better...`,
                      rows: 5,
                    }}
                  />
                </Stack>
              </Fieldset>
            </DialogBody>

            <DialogFooter hasDivider>
              <DialogClose asChild>
                <Button variant="outline" disabled={feedbackMutation.isPending}>
                  {`Cancel`}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                variant="brand-subtle"
                isPending={feedbackMutation.isPending}
              >
                {`Submit feedback`}
              </Button>
            </DialogFooter>
          </FormProvider>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
